import React, { useState, useEffect, useRef } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plane, FileText, Clock, CheckCircle, Eye, AlertCircle, Plus, ChevronRight, XCircle, Send, Loader2 } from 'lucide-react';
import Layout from '../components/Layout';
import Button from '../components/Button';
import Alert from '../components/Alert';
import { useClaimStore } from '../store/claimStore';
import { useEmbeddedApp } from '../providers/EmbeddedAppProvider';
import { RequestType, ClaimStatus } from '../types';
import { getAssistanceEventsWithoutClaim } from '../services/api';
import { logger } from '../utils/logger';
import { getClaimStatusLabel, getStatusLabelKey, isSystemProcessingStatus, isDeletedStatus } from '../utils/claimStatusDisplay';
import { formatDateShortOrNA } from '../utils/dateUtils';

const HomeScreen: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { getDrafts, getSentClaims, fetchHomeClaims, initializeClaim } = useClaimStore();
  const claimsError = useClaimStore((state) => state.error);
  const { parentContext, isEmbedded } = useEmbeddedApp();

  const [isLoadingClaims, setIsLoadingClaims] = useState(false);

  const allDrafts = getDrafts();
  const allSentClaims = getSentClaims();

  // Drafts arrive pre-sorted from the BFF (DEV-7027 bugfix): active DRAFT* first
  // sorted by lastModifiedDate DESC, ABANDONED* appended at the end. No client-side
  // partition needed. DELETED items are dropped server-side; the extra filter here
  // is defense-in-depth for DEV-7217.
  const drafts = allDrafts.filter(d => d != null && !isDeletedStatus(d.status?.toString()));

  const myClaims = allSentClaims.filter(c => !isDeletedStatus(c.status?.toString()));

  const [selectedDraft, setSelectedDraft] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | ClaimStatus>('all');
  const [activeTab, setActiveTab] = useState<'new' | 'drafts' | 'myClaims'>('new');

  // Track if context has been initialized to prevent multiple calls
  const contextInitializedRef = useRef(false);

  // Fetch unified claim list (drafts + sent) from API on mount — single BFF call
  useEffect(() => {
    let isMounted = true;

    const initializeScreen = async () => {
      setIsLoadingClaims(true);
      try {
        await fetchHomeClaims();

        if (isMounted) {
          const draftsCount = getDrafts().length;
          const sentCount = getSentClaims().length;
          logger.info('User landed on Homescreen', {
            screenCode: '00-00-HOME',
            language: i18n.language,
            hasActiveClaims: sentCount > 0,
            hasDraftClaims: draftsCount > 0,
          });
        }
      } finally {
        if (isMounted) {
          setIsLoadingClaims(false);
        }
      }
    };

    initializeScreen();

    return () => {
      isMounted = false;
    };
  }, []);

  // Pre-fill context from parent app when available
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    // Only initialize once when parentContext becomes available
    if (parentContext && isEmbedded && !contextInitializedRef.current) {
      contextInitializedRef.current = true;

      // Initialize claim store with parent context data (stored separately from stepData)
      // Use getState() to avoid triggering effect when store updates
      useClaimStore.getState().initializeFromParentContext({
        contracts: parentContext.contracts,
        clientId: parentContext.clientId,
        clientInfo: parentContext.clientInfo
      });
    }
  }, [parentContext, isEmbedded]); // Removed initializeFromParentContext from dependencies


  // Filter claims based on search term and status
  const filteredClaims = myClaims.filter(claim => {
    const matchesSearch = !searchTerm ||
      claim.eventNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.contractNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      claim.totalAmount?.toString().includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || claim.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  // Helper function to get status badge props.
  // Visuals are keyed off the canonical short label key from getStatusLabelKey,
  // not the raw status string — so DEV-6905 unified statuses (CRM_STATUS_*,
  // RESUMPTION_*, SYSTEM_PROCESSING) AND pre-DEV-7027 legacy values (DRAFT,
  // SUBMITTED, NEW, …) all map through the same suffix-collapsing path.
  const getStatusBadge = (status: ClaimStatus) => {
    const text = getClaimStatusLabel(status?.toString(), t);
    const key = getStatusLabelKey(status?.toString());

    const visuals: Record<string, { className: string; icon: typeof Send }> = {
      draft: { className: 'bg-yellow-100 text-yellow-800', icon: Clock },
      submitted: { className: 'bg-blue-100 text-blue-800', icon: Send },
      underReview: { className: 'bg-yellow-100 text-yellow-800', icon: Eye },
      documentsRequired: { className: 'bg-orange-100 text-orange-800', icon: FileText },
      processed: { className: 'bg-green-100 text-green-800', icon: CheckCircle },
      awaitingInformation: { className: 'bg-orange-100 text-orange-800', icon: AlertCircle },
      cancelled: { className: 'bg-red-100 text-red-800', icon: XCircle },
      systemProcessing: { className: 'bg-gray-100 text-gray-700', icon: Loader2 },
    };

    const visual = visuals[key] || { className: 'bg-gray-100 text-gray-800', icon: AlertCircle };
    return { text, ...visual };
  };

  // Helper function to get claim highlight class based on status
  const getClaimHighlight = (status: ClaimStatus) => {
    if (status === 'OPEN' || status === 'DRAFT') {
      return 'border-l-4 border-l-orange-500 bg-orange-50';
    }
    if (status === 'TO_BE_COMPLETED') {
      return 'border-l-4 border-l-red-500 bg-red-50';
    }
    return '';
  };

  // Group claims by submission year
  const groupClaimsByYear = (claims: typeof myClaims) => {
    const grouped: Record<number, typeof myClaims> = {};

    claims.forEach(claim => {
      if (claim.createdAt) {
        const year = new Date(claim.createdAt).getFullYear();
        if (!grouped[year]) {
          grouped[year] = [];
        }
        grouped[year].push(claim);
      }
    });

    // Sort years descending (most recent first)
    return Object.entries(grouped).sort(([yearA], [yearB]) => parseInt(yearB) - parseInt(yearA));
  };

  // Check if claim is eligible for adding expenses
  const canAddExpenses = (claim: typeof myClaims[0]) => {
    return claim.status === ClaimStatus.SUBMITTED ||
           claim.status === ClaimStatus.TO_BE_COMPLETED;
  };

  const handleNewClaim = async () => {
    try {
      // Get contract numbers from parent context
      const contractNumbers = parentContext?.contracts?.map(c => c.contractNumber) || [];

      if (contractNumbers.length === 0) {
        // No contracts available - continue normal flow
        initializeClaim(RequestType.NEW_CLAIM, { type: RequestType.NEW_CLAIM });
        navigate('/claim/00-01-NOUVELLE', {
          state: {
            selectedRequestType: RequestType.NEW_CLAIM,
            timestamp: new Date().toISOString()
          }
        });
        return;
      }

      // Check if assistance events exist without claim
      const assistanceEvents = await getAssistanceEventsWithoutClaim(contractNumbers);

      if (assistanceEvents.length > 0) {
        // Navigate to EventSelectionScreen
        navigate('/claim/00-00-SELECTION');
      } else {
        // No assistance events - continue normal flow
        initializeClaim(RequestType.NEW_CLAIM, { type: RequestType.NEW_CLAIM });
        navigate('/claim/00-01-NOUVELLE', {
          state: {
            selectedRequestType: RequestType.NEW_CLAIM,
            timestamp: new Date().toISOString()
          }
        });
      }
    } catch (error) {
      logger.error('Error checking assistance events', {
        screenCode: '00-00-HOME',
      }, error as Error);
      // On error, default to normal flow
      initializeClaim(RequestType.NEW_CLAIM, { type: RequestType.NEW_CLAIM });
      navigate('/claim/00-01-NOUVELLE', {
        state: {
          selectedRequestType: RequestType.NEW_CLAIM,
          timestamp: new Date().toISOString()
        }
      });
    }
  };

  return (
    <Layout>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-50">
        <div className="max-w-7xl mx-auto sm:px-6 lg:px-8 sm:py-8">
          {/* Enhanced Hero Section - Mobile Optimized */}
          <div className="text-center mb-6 xs:mb-8 sm:mb-12 lg:mb-16">
            <h1 className="text-2xl xs:text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-tenant-800 mb-3 xs:mb-4 sm:mb-6 leading-tight px-2 xs:px-4">
              {isEmbedded ? t('embedded.welcome') : t('home.title')}
            </h1>
            <p className="text-lg text-brand-gray-600">{t('embedded.introductionMessage')}</p>
          </div>

          {/* Tab Navigation - Mobile Optimized */}
          <div className="max-w-4xl mx-auto mb-6 xs:mb-8 sm:px-4">
            <div className="bg-white rounded-xl shadow-sm border border-brand-gray-300 overflow-hidden">
              <div className="flex border-b border-brand-gray-300 overflow-x-auto scrollbar-hide">
                {/* Tab 1: Nouvelle Demande (New Claim) */}
                <button
                  onClick={() => setActiveTab('new')}
                  className={`flex-1 min-w-0 px-2 xs:px-3 sm:px-6 py-3 sm:py-4 text-center font-medium transition-colors relative ${
                    activeTab === 'new'
                      ? 'text-tenant-primary bg-tenant-50 border-b-2 border-tenant-primary'
                      : 'text-brand-gray-600 hover:text-tenant-800 hover:bg-tenant-50'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  <div className="flex flex-row items-center justify-center sm:space-x-2">
                    <Plane className="w-4 h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline text-sm font-medium whitespace-nowrap">{t('home.tabs.startNew')}</span>
                  </div>
                </button>

                {/* Tab 2: Demandes en cours (Drafts) */}
                <button
                  onClick={() => setActiveTab('drafts')}
                  className={`flex-1 min-w-0 px-2 xs:px-3 sm:px-6 py-3 sm:py-4 text-center font-medium transition-colors relative ${
                    activeTab === 'drafts'
                      ? 'text-tenant-primary bg-tenant-50 border-b-2 border-tenant-primary'
                      : 'text-brand-gray-600 hover:text-tenant-800 hover:bg-tenant-50'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  <div className="flex flex-row items-center justify-center sm:space-x-2">
                    <div className="flex items-center relative">
                      <Clock className="w-4 h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5" />
                      {drafts.length > 0 && (
                        <span className="absolute -top-2 -right-2 bg-draft-500 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                          {drafts.length}
                        </span>
                      )}
                    </div>
                    <span className="hidden sm:inline text-sm font-medium whitespace-nowrap sm:ml-0">{t('home.tabs.continueDraft')}</span>
                  </div>
                </button>

                {/* Tab 3: Mes réclamations (My Claims) */}
                <button
                  onClick={() => setActiveTab('myClaims')}
                  className={`flex-1 min-w-0 px-2 xs:px-3 sm:px-6 py-3 sm:py-4 text-center font-medium transition-colors relative ${
                    activeTab === 'myClaims'
                      ? 'text-tenant-primary bg-tenant-50 border-b-2 border-tenant-primary'
                      : 'text-brand-gray-600 hover:text-tenant-800 hover:bg-tenant-50'
                  }`}
                  style={{ minHeight: '48px' }}
                >
                  <div className="flex flex-row items-center justify-center sm:space-x-2">
                    <FileText className="w-4 h-4 xs:w-5 xs:h-5 sm:w-5 sm:h-5" />
                    <span className="hidden sm:inline text-sm font-medium whitespace-nowrap">{t('home.tabs.myClaims')}</span>
                  </div>
                </button>
              </div>

              {/* Tab Content - Mobile Optimized */}
              <div className="p-3 xs:p-4 sm:p-6 lg:p-8">
                {/* New Claim Tab - Mobile Optimized */}
                {activeTab === 'new' && (
                  <div className="text-center">
                    <div className="w-14 h-14 xs:w-16 xs:h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-tenant-primary rounded-2xl flex items-center justify-center mx-auto mb-3 xs:mb-4 sm:mb-6 shadow-lg">
                      <Plane className="w-6 h-6 xs:w-8 xs:h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                    </div>
                    <h2 className="text-lg xs:text-xl sm:text-2xl lg:text-3xl font-bold text-tenant-800 mb-2 xs:mb-3 sm:mb-4 px-2 xs:px-4">
                      {t('home.startNewClaim')}
                    </h2>
                    <div className="text-brand-gray-600 mb-4 xs:mb-6 sm:mb-8 max-w-2xl mx-auto text-sm xs:text-base sm:text-lg leading-relaxed px-2 xs:px-4 whitespace-pre-line">
                      {t('home.newClaim.description')}
                    </div>

                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleNewClaim}
                      className="w-full sm:w-auto px-4 xs:px-6 sm:px-12 py-3 sm:py-4 text-sm xs:text-base sm:text-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                      style={{ minHeight: '48px' }}
                    >
                      <Plane className="w-4 h-4 xs:w-5 xs:h-5 sm:w-6 sm:h-6 mr-1 xs:mr-2 sm:mr-3" />
                      {t('home.newClaim.button')}
                      <ChevronRight className="w-3 h-3 xs:w-4 xs:h-4 sm:w-5 sm:h-5 ml-1 xs:ml-2" />
                    </Button>
                  </div>
                )}

                {/* Draft Selection Tab - Mobile Optimized */}
                {activeTab === 'drafts' && (
                  <div>
                    <div className="flex items-center mb-4 sm:mb-6">
                      <Clock className="w-6 h-6 sm:w-8 sm:h-8 text-draft-600 mr-3" />
                      <div>
                        <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                          {t('home.drafts.title')}
                        </h3>
                        <p className="text-gray-600 text-sm sm:text-base">{t('home.drafts.subtitle')}</p>
                      </div>
                    </div>

                    {isLoadingClaims ? (
                      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                        {/* Loading skeleton */}
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 animate-pulse">
                            <div className="flex items-start space-x-3 sm:space-x-4">
                              <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gray-300 rounded-full mt-1"></div>
                              <div className="flex-1 space-y-3">
                                <div className="h-5 bg-gray-300 rounded w-3/4"></div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : claimsError ? (
                      <div className="py-16 bg-gray-50 rounded-xl px-4">
                        <Alert type="error" message={t('home.claims.errorLoading')} />
                      </div>
                    ) : drafts.length > 0 ? (
                      <>
                        <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                          {drafts.filter(draft => draft != null).map((draft) => {
                            const isSelected = selectedDraft === draft?.id;
                            const isCancelled = getStatusLabelKey(draft?.status) === 'cancelled';
                            const draftBadge = getStatusBadge(draft?.status as ClaimStatus);
                            const DraftBadgeIcon = draftBadge.icon;

                            return (
                              <div
                                key={draft?.id || Math.random()}
                                className={`bg-white border-2 rounded-xl p-4 sm:p-6 transition-all duration-200 ${
                                  isCancelled
                                    ? 'border-red-200 bg-red-50 opacity-75 cursor-not-allowed'
                                    : isSelected
                                    ? 'border-draft-500 bg-draft-50 shadow-md cursor-pointer hover:shadow-lg'
                                    : 'border-gray-200 hover:border-draft-300 cursor-pointer hover:shadow-lg'
                                }`}
                                onClick={() => !isCancelled && draft?.id && setSelectedDraft(draft.id)}
                              >
                                <div className="flex items-start space-x-3 sm:space-x-4">
                                  <input
                                    type="radio"
                                    name="draft"
                                    value={draft?.id || ''}
                                    checked={isSelected}
                                    onChange={() => !isCancelled && draft?.id && setSelectedDraft(draft.id)}
                                    disabled={isCancelled}
                                    className="w-4 h-4 sm:w-5 sm:h-5 mt-1 text-draft-600 focus:ring-draft-500 border-gray-300 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                    style={{ minWidth: '16px', minHeight: '16px' }}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div className="flex-1 min-w-0">
                                    {/* Header with case number and status badge */}
                                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                                      <div className="flex-1">
                                        <h4 className={`text-base sm:text-lg font-semibold mb-2 ${isCancelled ? 'text-gray-500 line-through' : 'text-gray-900'}`}>
                                          {t('home.claims.caseNumber')}: {draft?.eventNumber || t('common.notAvailable')}
                                        </h4>
                                      </div>
                                      <div className="flex flex-col items-end gap-1">
                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit ${draftBadge.className}`}>
                                          <DraftBadgeIcon className="w-3 h-3 mr-1" />
                                          {draftBadge.text}
                                        </span>
                                      </div>
                                    </div>

                                    {/* Claim details grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs sm:text-sm">
                                      {/* Contract number */}
                                      <div className="text-gray-600">
                                        <span className="font-medium">{t('home.claims.contractNumber')}:</span> {draft?.contractNumber || t('common.notAvailable')}
                                      </div>

                                      {/* Claim type */}
                                      <div className="text-gray-600">
                                        <span className="font-medium">{t('home.claims.type')}:</span> {draft?.claimType ? t(`progress.banner.claimTypes.${draft.claimType}`) : t('common.notAvailable')}
                                      </div>

                                      {/* Created date */}
                                      <div className="text-gray-600">
                                        <span className="font-medium">{t('home.claims.createdDate')}:</span> {formatDateShortOrNA(draft?.createdAt)}
                                      </div>

                                      {/* Event date */}
                                      {draft?.incidentDate ? (
                                        <div className="text-gray-600">
                                          <span className="font-medium">{t('home.claims.eventDate')}:</span> {formatDateShortOrNA(draft.incidentDate)}
                                        </div>
                                      ) : null}

                                      {/* Last modified date */}
                                      <div className="text-gray-600">
                                        <span className="font-medium">{t('home.claims.lastModified')}:</span> {formatDateShortOrNA(draft?.lastModified)}
                                      </div>

                                      {/* Beneficiary */}
                                      {(draft?.beneficiaryFirstName || draft?.beneficiaryLastName) ? (
                                        <div className="text-gray-600">
                                          <span className="font-medium">{t('home.claims.beneficiary')}:</span> {`${draft?.beneficiaryFirstName || ''} ${draft?.beneficiaryLastName || ''}`.trim()}
                                        </div>
                                      ) : null}

                                      {/* Total amount */}
                                      <div className="text-gray-900 font-semibold">
                                        <span className="font-medium">{t('home.claims.totalAmount')}:</span> ${draft?.totalAmount?.toFixed(2) || '0.00'} CAD
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (selectedDraft) {
                              const selectedDraftData = drafts.find(d => d?.id === selectedDraft);
                              // Only navigate if the selected draft isn't abandoned/cancelled.
                              // getStatusLabelKey covers ABANDONED, RESUMPTION_ABANDONED, CANCELLED,
                              // CRM_STATUS_CANCELLED, RESUMPTION_CRM_STATUS_CANCELLED — all collapse
                              // to 'cancelled'.
                              if (selectedDraftData && getStatusLabelKey(selectedDraftData.status) !== 'cancelled') {
                                navigate(`/claim/00-10-RECLAMATIONS?id=${selectedDraft}`);
                              }
                            }
                          }}
                          disabled={!selectedDraft || getStatusLabelKey(drafts.find(d => d?.id === selectedDraft)?.status) === 'cancelled'}
                          size="lg"
                          className="w-full font-semibold py-3 sm:py-4"
                          style={{ minHeight: '48px' }}
                        >
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          {t('home.drafts.viewDetails')}
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-16 bg-gray-50 rounded-xl">
                        <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-xl font-semibold text-gray-600 mb-2">
                          {t('home.drafts.noDrafts')}
                        </h4>
                        <p className="text-gray-500 mb-6">
                          {t('home.drafts.noDraftsDescription')}
                        </p>
                        <Button
                          variant="secondary"
                          onClick={() => setActiveTab('new')}
                        >
                          {t('home.newClaim.button')}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* My Claims Tab - Mobile Optimized */}
                {activeTab === 'myClaims' && (
                  <div>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6">
                      <div className="flex items-center mb-3 sm:mb-0">
                        <Plus className="w-6 h-6 sm:w-8 sm:h-8 text-green-600 mr-3" />
                        <div>
                          <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                            {t('home.claims.title')}
                          </h3>
                          <div className="text-gray-600 text-sm sm:text-base">
                            <p>{t('home.claims.subtitleIntro')}</p>
                            <ul className="list-disc list-inside mt-1 space-y-0.5">
                              <li>
                                <Trans i18nKey="home.claims.subtitleActions.view" components={{ bold: <strong /> }} />
                              </li>
                              <li>
                                <Trans i18nKey="home.claims.subtitleActions.add" components={{ bold: <strong /> }} />
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Enhanced Claims List */}
                    {isLoadingClaims ? (
                      <div className="space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                        {/* Loading skeleton */}
                        {[1, 2].map((i) => (
                          <div key={i} className="bg-white border-2 border-gray-200 rounded-xl p-4 sm:p-6 animate-pulse">
                            <div className="flex items-start space-x-3 sm:space-x-4">
                              <div className="w-4 h-4 sm:w-5 sm:h-5 bg-gray-300 rounded-full mt-1"></div>
                              <div className="flex-1 space-y-3">
                                <div className="flex justify-between items-start">
                                  <div className="h-5 bg-gray-300 rounded w-2/3"></div>
                                  <div className="h-6 bg-gray-300 rounded-full w-20"></div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : claimsError ? (
                      <div className="py-16 bg-gray-50 rounded-xl px-4">
                        <Alert type="error" message={t('home.claims.errorLoading')} />
                      </div>
                    ) : filteredClaims.length === 0 ? (
                      <div className="text-center py-16 bg-gray-50 rounded-xl">
                        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                        <h4 className="text-xl font-semibold text-gray-600 mb-2">
                          {t('home.claims.noClaims')}
                        </h4>
                        <p className="text-gray-500 mb-6">
                          {searchTerm || statusFilter !== 'all' 
                            ? t('home.claims.noMatchDescription') 
                            : t('home.claims.noClaimsDescription')}
                        </p>
                        {(searchTerm || statusFilter !== 'all') && (
                          <Button
                            variant="secondary"
                            onClick={() => {
                              setSearchTerm('');
                              setStatusFilter('all');
                            }}
                          >
                            {t('home.claims.clearFilters')}
                          </Button>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="space-y-6 mb-6 sm:mb-8">
                          {/* Group claims by year */}
                          {groupClaimsByYear(filteredClaims).map(([year, yearClaims]) => (
                            <div key={year} className="space-y-3 sm:space-y-4">
                              {/* Year header */}
                              <h3 className="text-xl font-bold text-gray-800 border-b-2 border-gray-300 pb-2">
                                {year}
                              </h3>

                              {/* Claims for this year */}
                              {yearClaims.map((claim) => {
                                const statusBadge = getStatusBadge(claim.status!);
                                const StatusIcon = statusBadge.icon;
                                const isSelected = selectedClaim === claim.id;
                                const eligible = canAddExpenses(claim);
                                const highlightClass = getClaimHighlight(claim.status!);
                                // SYSTEM_PROCESSING / RESUMPTION_SYSTEM_PROCESSING — DEV-7027 absorbed
                                // the previous `claim.isSystemProcessing` flag into the unified status.
                                const isProcessing = isSystemProcessingStatus(claim.status);

                                return (
                                  <div
                                    key={claim.id}
                                    className={`bg-white border-2 rounded-xl p-4 sm:p-6 transition-all duration-200 ${highlightClass} ${
                                      isProcessing
                                        ? 'border-blue-200 bg-blue-50/40 opacity-60 cursor-not-allowed'
                                        : isSelected
                                          ? 'border-tenant-500 bg-tenant-50 shadow-md cursor-pointer hover:shadow-lg'
                                          : 'border-gray-200 hover:border-tenant-300 cursor-pointer hover:shadow-lg'
                                    }`}
                                    onClick={() => !isProcessing && setSelectedClaim(claim.id!)}
                                    title={isProcessing ? t('home.status.systemProcessingTooltip') : undefined}
                                  >
                                    <div className="flex items-start space-x-3 sm:space-x-4">
                                      <input
                                        type="radio"
                                        name="claim"
                                        value={claim.id}
                                        checked={isSelected}
                                        onChange={() => !isProcessing && setSelectedClaim(claim.id!)}
                                        disabled={isProcessing}
                                        className="w-4 h-4 sm:w-5 sm:h-5 mt-1 text-tenant-primary focus:ring-tenant-primary border-gray-300 flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{ minWidth: '16px', minHeight: '16px' }}
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                      <div className="flex-1 min-w-0">
                                        {/* Header with case number and status badge */}
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-3">
                                          <div className="flex-1">
                                            <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
                                              {t('home.claims.caseNumber')}: {claim.eventNumber || t('common.notAvailable')}
                                            </h4>
                                          </div>
                                          <div className="flex flex-col items-end gap-1">
                                            {isProcessing ? (
                                              <span
                                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                                                title={t('home.status.systemProcessingTooltip')}
                                              >
                                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                {t('home.status.systemProcessing')}
                                              </span>
                                            ) : (
                                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium w-fit ${statusBadge.className}`}>
                                                <StatusIcon className="w-3 h-3 mr-1" />
                                                {statusBadge.text}
                                              </span>
                                            )}
                                          </div>
                                        </div>

                                        {/* Claim details grid */}
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3 text-xs sm:text-sm">
                                          {/* Contract number */}
                                          <div className="text-gray-600">
                                            <span className="font-medium">{t('home.claims.contractNumber')}:</span> {claim.contractNumber || t('common.notAvailable')}
                                          </div>

                                          {/* Claim type */}
                                          <div className="text-gray-600">
                                            <span className="font-medium">{t('home.claims.type')}:</span> {claim.claimType ? t(`progress.banner.claimTypes.${claim.claimType}`) : t('common.notAvailable')}
                                          </div>

                                          {/* Created date */}
                                          <div className="text-gray-600">
                                            <span className="font-medium">{t('home.claims.createdDate')}:</span> {formatDateShortOrNA(claim.createdAt)}
                                          </div>

                                          {/* Event date */}
                                          {claim?.incidentDate ? (
                                            <div className="text-gray-600">
                                              <span className="font-medium">{t('home.claims.eventDate')}:</span> {formatDateShortOrNA(claim.incidentDate)}
                                            </div>
                                          ) : null}

                                          {/* Last modified date */}
                                          <div className="text-gray-600">
                                            <span className="font-medium">{t('home.claims.lastModified')}:</span> {formatDateShortOrNA(claim.lastModified)}
                                          </div>

                                          {/* Beneficiary */}
                                          {(claim.beneficiaryFirstName || claim.beneficiaryLastName) ? (
                                            <div className="text-gray-600">
                                              <span className="font-medium">{t('home.claims.beneficiary')}:</span> {`${claim.beneficiaryFirstName || ''} ${claim.beneficiaryLastName || ''}`.trim()}
                                            </div>
                                          ) : null}

                                          {/* Total amount */}
                                          <div className="text-gray-900 font-semibold">
                                            <span className="font-medium">{t('home.claims.totalAmount')}:</span> ${claim.totalAmount?.toFixed(2) || '0.00'} CAD
                                          </div>
                                        </div>

                                        {/* Eligibility indicator - hidden during NOVA pre-processing transient (DEV-6905) */}
                                        {!isProcessing && (
                                          <div className="flex flex-wrap gap-2">
                                            {eligible && (
                                              <div className="flex items-center text-xs sm:text-sm text-green-600 bg-green-50 px-2 py-1 rounded-lg w-fit">
                                                <Plus className="w-3 h-3 mr-1" />
                                                {t('home.claims.eligible')}
                                              </div>
                                            )}
                                            {(claim.status === ClaimStatus.SUBMITTED || claim.status === ClaimStatus.TO_BE_COMPLETED) && (
                                              <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-tenant-100 text-tenant-800 rounded-full">
                                                <CheckCircle className="w-3 h-3 mr-1" />
                                                Eligible for additional expenses
                                              </span>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          ))}
                        </div>
                        
                        <Button
                          variant="primary"
                          onClick={() => {
                            if (!selectedClaim) return;
                            const selected = myClaims.find(c => c.id === selectedClaim);
                            // Block navigation while NOVA pre-processing the submission (DEV-6905).
                            if (isSystemProcessingStatus(selected?.status)) return;
                            navigate(`/claim/00-10-RECLAMATIONS?id=${selectedClaim}`);
                          }}
                          disabled={!selectedClaim || isSystemProcessingStatus(myClaims.find(c => c.id === selectedClaim)?.status)}
                          size="lg"
                          className="w-full font-semibold py-3 sm:py-4"
                          style={{ minHeight: '48px' }}
                        >
                          <Eye className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                          {t('home.claims.viewDetails')}
                          <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 ml-2" />
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default HomeScreen;